import { Link } from "@tanstack/react-router";
import { Lock, Sparkles, X } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useGuestUnlockTrigger } from "@/lib/guest/useGuestUnlockTrigger";
import { trackEvent } from "@/lib/audit";

/**
 * F2 — Unlock Experience Layer.
 * Soft conversion prompt that appears after a guest engages with the app.
 * It expands access naturally instead of acting as a registration wall.
 */
export function UnlockExperienceModal() {
  const { t } = useLanguage();
  const { shouldShow, dismiss } = useGuestUnlockTrigger();

  if (!shouldShow) return null;

  const unlocks = [
    t("unlock_feature_1"),
    t("unlock_feature_2"),
    t("unlock_feature_3"),
    t("unlock_feature_4"),
  ];

  return (
    <div className="fixed inset-0 z-[75] flex items-end justify-center bg-background/70 px-3 pb-6 backdrop-blur-sm sm:items-center sm:pb-0">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-b from-[oklch(0.20_0.04_280)]/95 to-[oklch(0.10_0.03_280)]/95 p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary">
            <Sparkles className="h-3 w-3" /> {t("unlock_eyebrow")}
          </span>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("dd_close")}
            className="rounded-full p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h2 className="mt-3 text-xl font-black text-foreground">{t("unlock_title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("unlock_sub")}</p>

        <ul className="mt-4 space-y-1.5 text-sm">
          {unlocks.map((feat) => (
            <li key={feat} className="flex items-start gap-2">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="text-foreground">{feat}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Link
            to="/registro"
            onClick={() => void trackEvent("guest_unlock_cta_clicked", { cta: "register" })}
            className="flex-1 rounded-xl bg-primary px-4 py-3 text-center text-sm font-bold text-primary-foreground shadow"
          >
            {t("unlock_cta_register")}
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-xl border border-border bg-background px-4 py-3 text-sm font-bold text-foreground"
          >
            {t("unlock_cta_later")}
          </button>
        </div>
      </div>
    </div>
  );
}